import { Box, Container, Flex } from 'theme-ui';
import React, { useRef, useState, useEffect } from 'react';
import useMatchMedia from '~/hooks/utils/use-match-media';
import { forwardRef } from 'react';
import CoverMedia from './CoverMedia';
import CoverBody from './CoverBody';
import useWindowSize from '~/hooks/components/use-window-size';
import VideoPreview from '../VideoPreview';
import useNavigationHeight from '~/context/NavigationContext/useNavigationHeight';
import LinkRenderer from '../LinkRenderer';
import ConditionalWrapper from '../ConditionalWrapper';

type Props = {
  coverFeature: Queries.CoverFeatureFragmentFragment;
  isAtTop: boolean;
  transparentNav: boolean;
};
const CoverFeature = forwardRef<HTMLDivElement, Props>(function CoverFeature(
  {
    coverFeature: {
      subHeaderList,
      subtitleList,
      subHeaderImages,
      titleSmall,
      layeredImageTitleText,
      layeredImageTextOverlap,
      layeredTextOrder,
      titleLarge,
      coverMobileTitle,
      mobileButtonLayout,
      body,

      // adjust color behind cover feature
      backgroundColor,
      // Adjust whether copy is inside cover feature or right below
      separateCopy = false,
      // adjust whether buttons and text is black or white.
      contentColor = 'Black',
      // alignment affects coverFeature content in mobile
      alignment = 'left',
      // mediaType indicates type of media that will run on homepage
      verticalAlignment,
      mediaType = 'Images',
      // will either be static image, carousel, or randomly generated image depending on mediaType
      coverImages, // Layout = FULL_WIDTH
      mobileCoverImages,
      layeredImages, // Layout = CONSTRAINED
      layeredMobileImages,
      // an array of cta's (max is 3)
      callToAction,
      // Extends coverfeature to fit window height
      fullHeight = false,
      muxVideo,
      mainVideo,
      carouselAutoplaySpeed,
      showCarouselControls,
      clickableMedia,
      clickableMediaPath,
      clickableMediaTitle,
    },
    // If true, cover feature will use notification height as padding top
    isAtTop = false,
    transparentNav = false,
  },
  ref
) {
  const copyHeightRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mountVideoNode, setMountVideoNode] = useState(false);
  const { height: windowHeight } = useWindowSize();

  const [paused, setPaused] = useState(true);

  const toggleVideo = () => {
    videoRef.current && videoRef.current.pause();
    setPaused((prev) => !prev);
  };

  const handleClick = () => {
    setMountVideoNode(true);
    if (!mountVideoNode) return;
    toggleVideo();
  };

  const alignItems =
    alignment === 'Center' ? 'center' : alignment === 'Left' ? 'start' : alignment === 'Right' ? 'end' : 'start';

  const isMobile = useMatchMedia('(max-width: 768px)');

  useEffect(() => {
    if (mountVideoNode) handleClick();
  }, [mountVideoNode]);

  const layered = mediaType && /layered images/i.test(mediaType);
  const noMedia = mediaType === 'None';

  const getMinHeight = () => {
    if (separateCopy && fullHeight) return 'auto';
    if (noMedia) return 0;
    return [videoRef.current?.clientHeight, 0];
  };

  const shouldScaleWithNotificationBar = isAtTop && (fullHeight || separateCopy);

  const heightOffset = shouldScaleWithNotificationBar ? 'var(--notification-height)' : '0px';

  const bodyCopyHeight = copyHeightRef?.current?.clientHeight;

  // if content height + 100px (padding + address bar changes) is more the 100vh/100svh set content height
  // 60px is for padding top + bottom
  const longCopy = bodyCopyHeight && bodyCopyHeight + 100 > (windowHeight ?? 0) ? `${bodyCopyHeight + 60}px` : false;

  const getCoverHeight = () => {
    if (noMedia) return 0;
    if (longCopy) return longCopy;
    if (fullHeight && !separateCopy) return `calc(100svh - ${heightOffset} - var(--nav-height))`;
    if (separateCopy && !fullHeight) return ['100%', '500px'];
    if (separateCopy && fullHeight) return null;
    return ['600px', null, layered ? '800px' : '650px'];
  };
  // separate function as this needs to be applied on the container
  const getCoverWithSeparateCopyHeight = () => {
    if (separateCopy && fullHeight) {
      if (transparentNav) return '100svh';
      else return 'calc(100svh - (var(--notification-height) + var(--nav-height)))';
    }
    return 'auto';
  };

  const { navHeight } = useNavigationHeight();

  return (
    <ConditionalWrapper
      condition={!!clickableMedia}
      wrapper={({ children }) => (
        <LinkRenderer slug={clickableMediaPath} title={clickableMediaTitle}>
          {children}
        </LinkRenderer>
      )}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          backgroundColor: backgroundColor,
          height: longCopy && !separateCopy ? longCopy : getCoverWithSeparateCopyHeight(),
          position: 'relative',
        }}
      >
        <Container
          variant="fullWidth"
          sx={{
            '--cover-height': getCoverHeight(),
            '@supports not (height: 100svh)': {
              /** fallback for browsers that don't support 100svh,
               * more reliable doing it here than in getCoverHeight function
               */
              '--cover-height': fullHeight ? `calc(100vh - ${heightOffset})` : getCoverHeight(),
            },
            aspectRatio: separateCopy && ['414/486', null, 'none'],
            width: '100%',
            position: 'relative',
            height: 'var(--cover-height)',
            '@media (orientation: landscape)': {
              height: paused ? fullHeight && ['var(--cover-height)'] : `calc(100svh - ${navHeight}px)`,
              minHeight: getMinHeight(),
            },
            '@media (orientation: portrait)': {
              height: fullHeight
                ? !paused && ['calc(100vh - 13.5rem)', 'calc(100vh - 6.4rem)', 'var(--cover-height)']
                : !paused && `calc(100svh - ${navHeight}px)`,
            },
            transition: '0.3s ease-out',
            isolation: 'isolate',
            '&:not(:hover)': {
              '.layered-images': {
                transform: 'scale(1)',
              },
              '.layered-text': { transform: 'scale(1)' },
            },
            flex: separateCopy && fullHeight ? '1 1 fit-content' : null,
            mx: 'auto',
            maxWidth: isAtTop ? 'none' : '1920px',
          }}
        >
          {!noMedia && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                backgroundColor: backgroundColor,
                borderRadius: 0,
                opacity: !paused ? 0 : 1,
                transition: 'opacity 2s ease',
                overflow: 'hidden',
                '.gatsby-plugin-image-wrapper:after': {
                  content: '""',
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                },

                '.gatsby-plugin-image-wrapper': { height: '100%' },
                zIndex: -1,
                position: 'relative',
              }}
            >
              <CoverMedia
                type={mediaType}
                images={isMobile ? mobileCoverImages ?? coverImages : coverImages ?? mobileCoverImages}
                layeredImages={isMobile ? layeredMobileImages ?? layeredImages : layeredImages}
                layeredImageTitleText={layeredImageTitleText}
                layeredImageTextOverlap={layeredImageTextOverlap}
                layeredTextOrder={layeredTextOrder}
                backgroundColor={backgroundColor}
                muxVideo={muxVideo}
                carouselAutoplaySpeed={carouselAutoplaySpeed}
                showCarouselControls={showCarouselControls}
              />
            </Box>
          )}
          {!separateCopy && (
            <CoverBody
              ref={ref}
              mobileButtonLayout={mobileButtonLayout}
              contentColor={contentColor}
              titleLarge={titleLarge}
              titleMobile={coverMobileTitle}
              titleSmall={titleSmall}
              body={body}
              callToAction={callToAction}
              subHeaderList={subHeaderList}
              subHeaderImages={subHeaderImages}
              subtitleList={subtitleList}
              fullHeight={fullHeight}
              alignment={alignment}
              verticalAlignment={verticalAlignment}
              alignItems={alignItems}
              separateCopy={false}
              paused={paused}
              handleClick={handleClick}
              copyHeightRef={copyHeightRef}
            />
          )}
        </Container>
        {muxVideo && mountVideoNode && (
          <VideoPreview
            paused={paused}
            mainVideo={mainVideo}
            setPaused={setPaused}
            setMountVideoNode={setMountVideoNode}
          />
        )}
        {separateCopy && (
          <Box
            sx={{
              flex: separateCopy && fullHeight ? '0 0 fit-content' : null,
            }}
          >
            <CoverBody
              ref={ref}
              mobileButtonLayout={mobileButtonLayout}
              contentColor={contentColor}
              titleLarge={titleLarge}
              titleMobile={coverMobileTitle}
              titleSmall={titleSmall}
              body={body}
              subHeaderImages={subHeaderImages}
              subHeaderList={subHeaderList}
              subtitleList={subtitleList}
              callToAction={callToAction}
              fullHeight={fullHeight}
              alignment={alignment}
              alignItems={alignItems}
              verticalAlignment={verticalAlignment}
              separateCopy={separateCopy}
              backgroundColor={backgroundColor}
              paused={paused}
              handleClick={handleClick}
              noMedia={noMedia}
              copyHeightRef={copyHeightRef}
            />
          </Box>
        )}
      </Flex>
    </ConditionalWrapper>
  );
});

export default React.memo(CoverFeature);
